import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  clearCheckoutAttempt,
  isCheckoutStale,
  markCheckoutCompleted,
  markCheckoutFailed,
  markCheckoutSyncing,
  readCheckoutAttempt,
  type CheckoutAttempt,
} from "@/lib/checkoutStatus";

/**
 * Stripe ödemesinin gerçek zamanlı durumunu izler.
 *
 * - `subscriptions` tablosundaki canlı olayları dinler; webhook kaydı düştüğü
 *   anda ödeme "tamamlandı" durumuna geçer ve erişim cache'leri tazelenir.
 * - Ödeme makul sürede sonuçlanmazsa (sekme kapatıldı, kart reddedildi vb.)
 *   durum otomatik olarak "failed" olur ve hata ekranı gösterilir.
 */
export const useCheckoutStatus = () => {
  const { user, refreshSubscription } = useAuth();
  const queryClient = useQueryClient();
  const [attempt, setAttempt] = useState<CheckoutAttempt | null>(() => readCheckoutAttempt());

  // localStorage değişimlerini (diğer sekme dahil) yansıt.
  useEffect(() => {
    const sync = () => setAttempt(readCheckoutAttempt());
    window.addEventListener("checkout-attempt-change", sync);
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("checkout-attempt-change", sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  const unlockCaches = useCallback(async () => {
    await refreshSubscription?.();
    for (const key of [
      ["active-subscriptions"],
      ["active-subscription"],
      ["stripe-subscription"],
      ["user-entitlements"],
      ["diet-plans"],
      ["support-videos"],
    ]) {
      queryClient.invalidateQueries({ queryKey: key });
    }
  }, [queryClient, refreshSubscription]);

  // Canlı abonelik olayları → ödeme tamamlandı.
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`checkout-status-${user.id}-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` },
        () => {
          const current = readCheckoutAttempt();
          if (!current || (current.phase !== "started" && current.phase !== "syncing")) return;
          const next = markCheckoutCompleted();
          setAttempt(next);
          void unlockCaches();
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, unlockCaches]);

  // Webhook gecikmesi fallback'i: canlı olay gelmezse Stripe'ı doğrudan sorgula.
  useEffect(() => {
    if (!attempt || (attempt.phase !== "started" && attempt.phase !== "syncing")) return;
    let cancelled = false;

    const poll = async () => {
      const current = readCheckoutAttempt();
      if (!current || (current.phase !== "started" && current.phase !== "syncing")) return;
      // İlk 15 sn realtime'a şans tanı, sonrasında "senkronizasyon bekleniyor" moduna geç.
      if (current.phase === "started" && Date.now() - current.startedAt < 15000) return;
      if (current.phase === "started") {
        const next = markCheckoutSyncing();
        if (!cancelled) setAttempt(next);
      }
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const { data } = await supabase.functions.invoke("check-subscription", {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        });
        if (!cancelled && data?.subscribed) {
          await unlockCaches();
          setAttempt(markCheckoutCompleted());
        }
      } catch {
        /* geçici hata: bir sonraki denemede tekrar sorgulanır */
      }
    };

    void poll();
    const id = setInterval(poll, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [attempt?.phase, attempt?.startedAt, unlockCaches]);

  // Zaman aşımı → hata ekranı.
  useEffect(() => {
    if (!attempt || (attempt.phase !== "started" && attempt.phase !== "syncing")) return;
    const check = () => {
      if (isCheckoutStale(readCheckoutAttempt())) {
        setAttempt(markCheckoutFailed("timeout"));
      }
    };
    check();
    const id = setInterval(check, 15000);
    return () => clearInterval(id);
  }, [attempt]);

  const dismiss = useCallback(() => {
    clearCheckoutAttempt();
    setAttempt(null);
  }, []);

  /** Hata sonrası kullanıcı manuel doğrulama isteyebilir. */
  const recheck = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data } = await supabase.functions.invoke("check-subscription", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (data?.subscribed) {
        await unlockCaches();
        setAttempt(markCheckoutCompleted());
        return true;
      }
    } catch {
      /* sessizce geç, hata ekranı açık kalır */
    }
    return false;
  }, [unlockCaches]);

  return { attempt, dismiss, recheck, waiting: attempt?.phase === "started" || attempt?.phase === "syncing" };
};
